/**
 * personDayDisplay.ts
 * Wave-3 Release 2 (percent retirement, spec H) — the FE mirror of the backend's
 * binary person-day model (backend/src/calculation/personDayModel.ts).
 *
 * The frontend does NOT import backend code, so this is a hand-kept mirror —
 * keep the two in lockstep. Decisions locked 2026-07-06 (do not re-litigate):
 *   - Q1 = B: per-day task-count chip; amber when a day carries MORE THAN
 *     DAY_TASK_COUNT_AMBER_THRESHOLD tasks.
 *   - Q5 = B: weekly utilization reads as PERCENT-OF-WEEK = busyDays ÷
 *     workingDays (holiday-aware). "60%" for 3 of 5 busy days — never "3/5 days"
 *     as the primary figure (the day fraction may appear as secondary text).
 *
 * Pure functions only — no React, no MUI, no API. Colors stay in the components
 * via theme.schedule.* tokens; this module only decides STATE and NUMBERS.
 */

// ---------------------------------------------------------------------------
// Constants (lockstep mirrors)
// ---------------------------------------------------------------------------

/**
 * Q1B — the day task-count chip turns amber when a person-day carries MORE
 * THAN this many tasks (>3 ⇒ amber). Mirrors DAY_TASK_COUNT_AMBER_THRESHOLD in
 * backend/src/calculation/personDayModel.ts — keep in lockstep.
 */
export const DAY_TASK_COUNT_AMBER_THRESHOLD = 3;

// ---------------------------------------------------------------------------
// Day state (the four working-day states of the binary model)
// ---------------------------------------------------------------------------

/** Display state of one working person-day. Non-working days are the caller's
 *  concern (weekend/holiday shading) — they never reach this mapper. */
export type PersonDayDisplayState = 'free' | 'busy' | 'absent' | 'conflict';

/**
 * Map a working day's additive wire fields (byDayTaskCount / byDayAbsent) to
 * its display state. `legacyLoad` is the legacy-scale per-day % (byDay) used
 * ONLY as a fallback when the additive fields are absent (stale backend):
 * any load then reads as plain "busy" — never absent/conflict, which the
 * legacy scale cannot distinguish reliably.
 */
export function personDayState(
  taskCount: number | undefined,
  absent: boolean | undefined,
  legacyLoad = 0,
): PersonDayDisplayState {
  if (taskCount === undefined && absent === undefined) {
    return legacyLoad > 0 ? 'busy' : 'free';
  }
  const tc = taskCount ?? 0;
  if (absent && tc > 0) return 'conflict';
  if (absent) return 'absent';
  if (tc > 0) return 'busy';
  return 'free';
}

/** Q1B — true when the day's task count should render the amber treatment. */
export function isTaskCountAmber(taskCount: number): boolean {
  return taskCount > DAY_TASK_COUNT_AMBER_THRESHOLD;
}

/**
 * Human label for a day state ("free" / "busy · 2 tests" / "day off" /
 * "conflict — assigned on a day off"). Used by tooltips and popup rows.
 */
export function personDayStateLabel(
  state: PersonDayDisplayState,
  taskCount = 0,
): string {
  switch (state) {
    case 'free':
      return 'free';
    case 'busy':
      return taskCount > 0
        ? `busy · ${taskCount} test${taskCount !== 1 ? 's' : ''}`
        : 'busy';
    case 'absent':
      return 'day off';
    case 'conflict':
      return 'conflict — assigned on a day off';
  }
}

// ---------------------------------------------------------------------------
// Weekly percent-of-week (Q5B)
// ---------------------------------------------------------------------------

/**
 * Q5B — weekly utilization as percent-of-week: busyDays ÷ workingDays × 100,
 * rounded. 3 busy days of a 5-day week → 60; a fully-busy 4-working-day
 * (holiday) week → 100. Returns 0 when the week has no working days.
 * Mirrors backend personDayModel.weekBusyPercent — keep in lockstep.
 */
export function weekBusyPercent(busyDays: number, workingDays: number): number {
  if (!Number.isFinite(workingDays) || workingDays <= 0) return 0;
  const b = Number.isFinite(busyDays) && busyDays > 0 ? busyDays : 0;
  return Math.round((b / workingDays) * 100);
}

/** Minimal weekly-cell shape shared by the capacity wire payloads. */
export interface WeekBusyCellFields {
  /** Legacy-scale weekly sum (busy day = 100, conflict day = 200). */
  totalPercent?: number;
  /** Wave-3 additive: count of busy working days (Q5B numerator). */
  busyDays?: number;
  /** Wave-3 additive: working days in the week, holiday-aware (denominator). */
  workingDays?: number;
}

/**
 * The weekly cell number (Q5B percent-of-week) from a capacity cell. Prefers
 * the additive busyDays/workingDays truth; falls back to the legacy-scale sum
 * ÷ (workingDays × 100), which equals the same percent under the R1 wire
 * (busy day = 100) except on conflict days — hence the preference.
 *
 * @param fallbackWorkingDays  Holiday-aware working days from the FE side
 *                             (workingDaysInWeek) for the legacy fallback.
 */
export function weekPercentFromCell(
  cell: WeekBusyCellFields | null | undefined,
  fallbackWorkingDays = 5,
): number {
  if (!cell) return 0;
  if (cell.busyDays !== undefined && cell.workingDays !== undefined) {
    return weekBusyPercent(cell.busyDays, cell.workingDays);
  }
  const wd = fallbackWorkingDays > 0 ? fallbackWorkingDays : 5;
  const sum = Number.isFinite(cell.totalPercent) && (cell.totalPercent ?? 0) > 0
    ? (cell.totalPercent as number)
    : 0;
  return Math.round(sum / wd);
}

/**
 * Secondary "busy X of Y days" fraction text (tooltips only — the primary
 * figure is always the Q5B percent). Null when the additive fields are absent.
 */
export function busyDayFractionLabel(cell: WeekBusyCellFields | null | undefined): string | null {
  if (!cell || cell.busyDays === undefined || cell.workingDays === undefined) return null;
  return `busy ${cell.busyDays} of ${cell.workingDays} day${cell.workingDays !== 1 ? 's' : ''}`;
}

/**
 * Weekly overload advisory (review-mandated, Q1B family): Σ per-day task
 * counts (= task-days) exceeding the week's working days means the person
 * carries more task-days than the week holds. Non-blocking, amber. Inputs are
 * the additive byDayTaskCount map + workingDays; false when either is absent.
 */
export function weekIsOverloaded(
  byDayTaskCount: Record<string, number> | undefined,
  workingDays: number | undefined,
): boolean {
  if (!byDayTaskCount || workingDays === undefined || workingDays <= 0) return false;
  let taskDays = 0;
  for (const v of Object.values(byDayTaskCount)) taskDays += v > 0 ? v : 0;
  return taskDays > workingDays;
}
